'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '@/app/api/client';
import { apiConfig } from '@/config/api';
import { Spinner } from '@/components/common/Spinner';
import { authClient } from '@/lib/auth-client';

export interface User {
  id: string;
  email: string;
  displayName?: string;
  photoUrl?: string;
  billing?: any;
  tenant?: any;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
  refreshUserStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper to manage cross-domain login signal cookie (kept for www site compatibility)
  const getRootDomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      // e.g. app.content-growth.com -> .content-growth.com
      return `.${parts.slice(-2).join('.')}`;
    }
    return hostname;
  };

  const setLoginSignalCookie = () => {
    try {
      const domain = getRootDomain();
      // Set for 7 days
      document.cookie = `login_signal=true; path=/; domain=${domain}; max-age=604800; SameSite=Lax`;
    } catch (e) {
      console.error('Failed to set login signal cookie:', e);
    }
  };

  const removeLoginSignalCookie = () => {
    try {
      const domain = getRootDomain();
      document.cookie = `login_signal=; path=/; domain=${domain}; max-age=0; SameSite=Lax`;
      document.cookie = `login_signal=; path=/; max-age=0; SameSite=Lax`;
    } catch (e) {
      console.error('[AuthContext] Failed to remove login signal cookie:', e);
    }
  };

  const fetchProfile = async () => {
    try {
      // API client now sends cookies automatically
      const { data } = await apiClient.get('/api/user/profile');
      console.log('[AuthContext] Profile loaded:', data);
      setUser(data.user);
      setLoginSignalCookie();
    } catch (error) {
      console.error('[AuthContext] Failed to fetch profile:', error);
      // specific handling?
    }
  };

  const checkSession = async () => {
    try {
      // First check if better-auth thinks we have a session
      const { data: session } = await authClient.getSession();

      if (session) {
        console.log('[AuthContext] Session found, fetching rich profile...');
        await fetchProfile();
      } else {
        console.log('[AuthContext] No session found.');
        setUser(null);
        removeLoginSignalCookie();
      }
    } catch (err) {
      console.error('[AuthContext] Session check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const logout = async () => {
    console.log('[AuthContext] logout() called');
    try {
      await authClient.signOut();

      // Clear local state
      removeLoginSignalCookie();
      setUser(null);

      // Redirect to marketing site login
      const redirectUrl = `${apiConfig.wwwUrl}/signin/?reauth=true`;
      console.log('[AuthContext] Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
    } catch (e) {
      console.error('Logout failed:', e);
      // Force redirect anyway
      window.location.href = `${apiConfig.wwwUrl}/signin/?reauth=true`;
    }
  };

  const refreshUserStatus = async () => {
    await fetchProfile();
  }

  const value = {
    user,
    loading,
    logout,
    refreshUserStatus
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8 text-blue-600" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
